import { getServiceApiErrorBody } from '@/hooks/getServiceApiErrorBody'

/**
 * Maps the delete-note / delete-patient-instruction API error codes (QMD-4870) to a
 * user-facing message. The Orval fetcher rejects with the API error body on `details`; we
 * read its `ErrorCode` and fall back to a generic message for anything unmapped.
 */
const MESSAGES: Record<string, string> = {
  ReasonNotValid: 'A reason is required.',
  Permission: 'You do not have permission to delete this record.',
  VisitNotFound: 'Visit not found.',
  NoteNotFound: 'This note no longer exists. It may already have been deleted.',
  PatientInstructionNotFound: 'This patient instruction no longer exists. It may already have been deleted.',
}

/** Codes meaning the record is already gone from the visit, so the shown list is stale. */
const RECORD_NOT_FOUND_CODES = new Set(['NoteNotFound', 'PatientInstructionNotFound'])

const hasErrorCode = (value: unknown): value is { ErrorCode: unknown } =>
  typeof value === 'object' && value !== null && 'ErrorCode' in value

function getErrorCode(error: unknown): string | undefined {
  const body = getServiceApiErrorBody(error)
  const code = hasErrorCode(body) ? body.ErrorCode : undefined
  return typeof code === 'string' ? code : undefined
}

export function getDeleteMedicalRecordErrorMessage(error: unknown): string {
  const code = getErrorCode(error)
  const mapped = code ? MESSAGES[code] : undefined

  return mapped ?? 'Something went wrong. Please try again.'
}

export function isMedicalRecordNotFoundError(error: unknown): boolean {
  const code = getErrorCode(error)
  return code !== undefined && RECORD_NOT_FOUND_CODES.has(code)
}
